import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	concatMap,
	filter,
	from,
	map,
	mergeMap,
	of,
	switchMap,
	tap,
	throwError,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsScopeService } from '../../ajs-scope.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { DataFrameRequester } from '../../requesters/core/data-frame.requester';
import { DatablockRequester } from '../../requesters/datablock/datablock.requester';
import {
	DatablockAction,
	DatablockActionPopupComponent,
	IDatablockDeleteModalData,
} from '../components/datablock-action-popup/datablock-action-popup.component';
import { DatablockItemEntity } from '../components/datablock-list/datablock-item.entity';
import { DatablockAdapter } from '../datablock.adapter';
import {
	bulkDelete,
	constructDatablockList,
	constructDatablockListFail,
	constructDatablockListSuccess,
	deleteDatablock,
	deleteDatablockFail,
	fetchDatablockListFail,
	fetchDatablockListSuccess,
	navigateToCreateFromDatablock,
	navigateToDatablock,
	navigateToDatablockCreate,
	navigateToDatablockDuplicate,
	navigateToExportDatablock,
	openDeleteConfirmationPopup,
	openPersistConfirmationPopup,
	openRepersistConfirmationPopup,
	openUnpersistConfirmationPopup,
	persistDatablock,
	persistDatablockFail,
	persistDatablockStarted,
	persistDatablockStopped,
	persistenceActionFinish,
	refreshDatablockList,
	repersistDatablock,
	repersistDatablockFail,
	repersistDatablockStarted,
	repersistDatablockStopped,
	stopPersistence,
	stopPersistenceFail,
	toggleDatablockStatus,
	trackAction,
	unpersistDatablock,
	unpersistDatablockFail,
	unpersistDatablockStarted,
	unpersistDatablockStopped,
	updateIncludePublic,
} from './datablock.actions';
import { DatablockSelector } from './datablock.selector';

interface IDatablockJobSocketResponse {
	element_id: number;
	type: string;
	message: string;
	data:
		| {
				job_status: string;
				client_id: string;
				job_id: string;
				persisted: boolean;
				persistence_date: number;
				persistence_execution_time: number;
				persistence_size: number;
				current_repartition_number: number;
		  }
		| undefined;
}
@Injectable()
export class DatablockEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToDatablockCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToDatablockCreate),
				tap(() => this.stateService.go(states.DatablockCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToDatablock = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToDatablock),
				tap((a) =>
					this.stateService.go(states.DatablockEdit, {
						id: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToDatablockDuplicate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToDatablockDuplicate),
				tap((a) =>
					this.stateService.go(states.DatablockDuplicate, {
						duplicatedId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToCreateFromDatablock = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToCreateFromDatablock),
				tap((a) =>
					this.stateService.go(states.DatablockFromDatablock, {
						datablockId: `${a.id}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToExportDatablock = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToExportDatablock),
				tap((a) =>
					this.stateService.go(states.ExportDatablockFromList, {
						datablockId: `${a.id}`,
						datablockLabel: a.label,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public constructDatablockList$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructDatablockList),
			map(() =>
				constructDatablockListSuccess({
					datablockList: this.datablockAdapter.generateDatablockListStructure(),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					constructDatablockListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public initDatablockList$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructDatablockListSuccess),
			withLatestFrom(this.datablockSelector.includePublic$()),
			switchMap(([, includePublic]) =>
				this.datablockRequester.getDatablockList(includePublic, true)
			),
			map((response) =>
				fetchDatablockListSuccess({
					datablockList: this.datablockAdapter.generateDatablockList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDatablockListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshDatablockList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshDatablockList),
			withLatestFrom(this.datablockSelector.includePublic$()),
			switchMap(([, includePublic]) =>
				this.datablockRequester.getDatablockList(includePublic, false)
			),
			map((response) =>
				fetchDatablockListSuccess({
					datablockList: this.datablockAdapter.generateDatablockList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDatablockListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateGetPublic$ = createEffect(() =>
		this.action$.pipe(
			ofType(updateIncludePublic),
			map(() => refreshDatablockList())
		)
	);

	public deleteDatablock$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteDatablock),
			switchMap((action) =>
				this.datablockRequester.deleteDatablock(action.datablockId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@datablock.delete.success:`
				);
				return refreshDatablockList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@datablock.delete.error:`
				);
				this.store.dispatch(deleteDatablockFail());
				return caught;
			})
		)
	);

	public bulkDeleteDatablocks$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDelete),
			switchMap((action) =>
				this.datablockRequester.deleteMany(action.datablockId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@datablock.delete.multiple.success:`
				);
				return refreshDatablockList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@datablock.delete.multiple.error:`
				);
				this.store.dispatch(refreshDatablockList());
				return caught;
			})
		)
	);

	public persistDatablock$ = createEffect(() =>
		this.action$.pipe(
			ofType(persistDatablock),
			concatMap((action) =>
				combineLatest([
					of(action.datablockId),
					this.dataFrameRequester.persist(action.datablockCode).pipe(
						catchError((error: HttpErrorResponse) =>
							throwError(() => ({
								error,
								datablockLabel: action.datablockLabel,
							}))
						)
					),
				])
			),
			map(([datablockId, payload]) =>
				trackAction({
					datablockId,
					jobId: payload.job_id,
				})
			),
			catchError(
				(
					customError: {
						error: HttpErrorResponse;
						datablockLabel: string;
						datablockId: number;
					},
					caught
				) => {
					const { error, datablockLabel } = customError;
					console.error(error);
					const errorMessage = $localize`:i18n=@@datablock.action.persist.error.message:
																			${datablockLabel}:DATABLOCK_NAME:`;
					this.ngCommService.notifyOnError(errorMessage);
					this.store.dispatch(
						persistDatablockFail({
							error,
						})
					);
					return caught;
				}
			)
		)
	);

	public repersistDatablock$ = createEffect(() =>
		this.action$.pipe(
			ofType(repersistDatablock),
			mergeMap((action) =>
				combineLatest([
					of(action.datablockId),
					this.dataFrameRequester.repersist(action.datablockCode).pipe(
						catchError((error: HttpErrorResponse) =>
							throwError(() => ({
								error,
								datablockLabel: action.datablockLabel,
							}))
						)
					),
				])
			),
			map(([datablockId, payload]) =>
				trackAction({
					datablockId,
					jobId: payload.job_id,
				})
			),
			catchError(
				(
					customError: { error: HttpErrorResponse; datablockLabel: string },
					caught
				) => {
					const { error, datablockLabel } = customError;
					console.error(error);
					const errorMessage = $localize`:i18n=@@datablock.action.repersist.error.message:
																			${datablockLabel}:DATABLOCK_NAME:`;
					this.ngCommService.notifyOnError(errorMessage);
					this.store.dispatch(
						repersistDatablockFail({
							error,
						})
					);
					return caught;
				}
			)
		)
	);

	public unpersistDatablock$ = createEffect(() =>
		this.action$.pipe(
			ofType(unpersistDatablock),
			concatMap((action) =>
				combineLatest([
					of(action.datablockId),
					this.dataFrameRequester.unpersist(action.datablockCode).pipe(
						catchError((error: HttpErrorResponse) =>
							throwError(() => ({
								error,
								datablockLabel: action.datablockLabel,
							}))
						)
					),
				])
			),
			map(([datablockId, payload]) =>
				trackAction({
					datablockId,
					jobId: payload.job_id,
				})
			),
			catchError(
				(
					customError: { error: HttpErrorResponse; datablockLabel: string },
					caught
				) => {
					const { error, datablockLabel } = customError;
					console.error(error);
					const errorMessage = $localize`:i18n=@@datablock.action.unpersist.error.message:
																			${datablockLabel}:DATABLOCK_NAME:`;
					this.ngCommService.notifyOnError(errorMessage);
					this.store.dispatch(
						unpersistDatablockFail({
							error, // FIXME: stop spinner if fail..
						})
					);
					return caught;
				}
			)
		)
	);

	public listenToWS$ = createEffect(() =>
		this.action$.pipe(
			ofType(constructDatablockListSuccess),
			switchMap(() =>
				from(
					this.ajsScopeService.listenOnRootScope<IDatablockJobSocketResponse>(
						'job_global'
					)
				)
			),
			filter(
				(stompResponse) =>
					stompResponse?.type === 'PersistFrame' ||
					stompResponse?.type === 'UnPersistFrame' ||
					stompResponse?.type === 'RepersistFrame'
			),
			withLatestFrom(this.datablockSelector.getDatablockList$()),
			tap(([stompResponse, datablockList]) => {
				if (stompResponse?.data?.job_status.toLocaleLowerCase() === 'running') {
					if (stompResponse?.type === 'PersistFrame') {
						this.store.dispatch(
							persistDatablockStarted({
								datablockId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
					if (stompResponse?.type === 'UnPersistFrame') {
						this.store.dispatch(
							unpersistDatablockStarted({
								datablockId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
					if (stompResponse?.type === 'RepersistFrame') {
						this.store.dispatch(
							repersistDatablockStarted({
								datablockId: stompResponse.element_id,
								jobId: stompResponse.data.job_id,
							})
						);
					}
				} else if (
					stompResponse?.data?.job_status.toLocaleLowerCase() === 'error'
				) {
					const { elements } = datablockList;
					const datablock = elements
						.toList()
						.toArray()
						.find((e) => e.code === stompResponse?.message);
					if (!datablock) {
						return;
					}

					let errorMessage = '';
					if (stompResponse?.type === 'PersistFrame') {
						errorMessage = $localize`:i18n=@@datablock.action.persist.error.message:
																			${datablock.label}:DATABLOCK_NAME:`;
						this.store.dispatch(
							persistDatablockStopped({
								datablockId: datablock.id,
								jobId: stompResponse.data.job_id,
							})
						);
					}

					if (stompResponse?.type === 'UnPersistFrame') {
						errorMessage = $localize`:i18n=@@datablock.action.unpersist.error.message:
																			${datablock.label}:DATABLOCK_NAME:`;
						this.store.dispatch(
							unpersistDatablockStopped({
								datablockId: datablock.id,
								jobId: stompResponse.data.job_id,
							})
						);
					}

					if (stompResponse?.type === 'RepersistFrame') {
						errorMessage = $localize`:i18n=@@datablock.action.repersist.error.message:
																			${datablock.label}:DATABLOCK_NAME:`;
						this.store.dispatch(
							repersistDatablockStopped({
								datablockId: datablock.id,
								jobId: stompResponse.data.job_id,
							})
						);
					}

					this.ngCommService.notifyOnError(errorMessage);
				}
			}),
			map(([stompResponse]) => stompResponse),
			filter(
				(stompResponse) =>
					stompResponse?.data?.job_status.toLocaleLowerCase() !== 'running' &&
					stompResponse?.data?.job_status.toLocaleLowerCase() !== 'waiting'
			),
			map((stompResponse) =>
				persistenceActionFinish({
					datablockId: stompResponse.element_id,
					persistenceInfo: {
						isPersisted: stompResponse.data?.persisted ?? false,
						persistenceDuration:
							stompResponse.data?.persistence_execution_time ?? 0,
						persistenceSize: stompResponse.data?.persistence_size ?? 0,
						persistenceDate: stompResponse.data?.persistence_date
							? new Date(stompResponse.data.persistence_date)
							: null,
						partitionCount: stompResponse.data?.current_repartition_number ?? 0,
					},
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshDatablockList());
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleDatablockStatus),
			switchMap((action) =>
				this.datablockRequester.getDatablock(action.datablockId)
			),
			switchMap((datablock) =>
				this.datablockRequester.updateDatablock({
					...datablock,
					metadata: {
						...datablock.metadata,
						actif: !datablock.metadata?.actif,
					},
				})
			),
			map(() => refreshDatablockList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshDatablockList());
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################

	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IDatablockDeleteModalData>(
					DatablockActionPopupComponent,
					{
						title: $localize`:i18n=@@datablock.action.popup.delete.header:`,
						subTitle: '',
						datablockId: action.datablockId,
						element: action.datablockLabel,
						action: DatablockAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.datablockId.length === 1) {
					return deleteDatablock({
						datablockId: action.datablockId[0],
					});
				} else {
					return bulkDelete({
						datablockId: action.datablockId,
					});
				}
			})
		)
	);

	public openPersistConfirmationPopup$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(openPersistConfirmationPopup),
				switchMap((action) => {
					const modalRef =
						this.modalService.openPopup<IDatablockDeleteModalData>(
							DatablockActionPopupComponent,
							{
								title: $localize`:i18n=@@datablock.action.popup.persist.header:`,
								subTitle: '',
								datablockId: action.datablockId,
								element: action.datablockLabel,
								action: DatablockAction.Persist,
							},
							{
								disableClose: true,
							}
						);
					return combineLatest([of(action), modalRef.afterClosed()]);
				}),
				filter(([, modalResult]) => modalResult !== undefined),
				withLatestFrom(this.datablockSelector.getDatablockList$()),
				map(([[action], list]) =>
					action.datablockId
						.map((id) => list.elements.get(`${id}`))
						.filter((d): d is DatablockItemEntity => d !== undefined)
						.forEach((d) => {
							if (!d.persistenceState.isPersisted) {
								return this.store.dispatch(
									persistDatablock({
										datablockId: d.id,
										datablockCode: d.code,
										datablockLabel: d.label,
									})
								);
							}
						})
				)
			),
		{
			dispatch: false,
		}
	);

	public openRepersistConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openRepersistConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IDatablockDeleteModalData>(
					DatablockActionPopupComponent,
					{
						title: $localize`:i18n=@@datablock.action.popup.repersist.header:`,
						subTitle: '',
						datablockId: action.datablockId,
						element: action.datablockLabel,
						action: DatablockAction.Repersist,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			withLatestFrom(this.datablockSelector.getDatablockList$()),
			map(([[action], list]) =>
				repersistDatablock({
					datablockId: action.datablockId,
					datablockCode: (
						list.elements.get(
							action.datablockId.toString()
						) as DatablockItemEntity
					).code,
					datablockLabel: action.datablockLabel,
				})
			)
		)
	);

	public openUnpersistConfirmationPopup$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(openUnpersistConfirmationPopup),
				switchMap((action) => {
					const modalRef =
						this.modalService.openPopup<IDatablockDeleteModalData>(
							DatablockActionPopupComponent,
							{
								title: $localize`:i18n=@@datablock.action.popup.depersist.header:`,
								subTitle: '',
								datablockId: action.datablockId,
								element: action.datablockLabel,
								action: DatablockAction.Depersist,
							},
							{
								disableClose: true,
							}
						);
					return combineLatest([of(action), modalRef.afterClosed()]);
				}),
				filter(([, modalResult]) => modalResult !== undefined),
				withLatestFrom(this.datablockSelector.getDatablockList$()),
				map(([[action], list]) =>
					action.datablockId
						.map((id) => list.elements.get(`${id}`))
						.filter((d): d is DatablockItemEntity => d !== undefined)
						.filter((d) => d.persistenceState.isPersisted)
						.forEach((d) =>
							this.store.dispatch(
								unpersistDatablock({
									datablockId: d.id,
									datablockCode: d.code,
									datablockLabel: d.label,
								})
							)
						)
				)
			),
		{
			dispatch: false,
		}
	);

	public stopOngoingPersistence$ = createEffect(() =>
		this.action$.pipe(
			ofType(stopPersistence),
			switchMap((action) =>
				this.datablockRequester.stopPersistence(action.jobKey)
			),
			map(() => refreshDatablockList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					stopPersistenceFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public constructor(
		private readonly action$: Actions,
		private readonly datablockSelector: DatablockSelector,
		private readonly datablockRequester: DatablockRequester,
		private readonly datablockAdapter: DatablockAdapter,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService,
		private readonly dataFrameRequester: DataFrameRequester,
		private readonly ajsScopeService: AjsScopeService
	) {}
}
